import type { DashboardSummaryInput } from '@repo/api-contracts';

export const DASHBOARD_PERIOD_PRESETS = ['today', '7d', '30d', 'custom'] as const;
export type DashboardPeriodPreset = (typeof DASHBOARD_PERIOD_PRESETS)[number];

export const DASHBOARD_PERIOD_PRESET_LABELS: Readonly<Record<DashboardPeriodPreset, string>> = {
  today: '오늘',
  '7d': '최근 7일',
  '30d': '최근 30일',
  custom: '기간 지정',
};

/** 화면의 기간 선택 — 서울 날짜(YYYY-MM-DD) 둘, `to` 는 포함이다. */
export interface DashboardPeriodSelection {
  readonly preset: DashboardPeriodPreset;
  readonly from: string;
  readonly to: string;
}

const DAY_MS = 24 * 60 * 60 * 1000;
const SEOUL_DATE = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Asia/Seoul',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/** 서울 날짜 YYYY-MM-DD. */
export const seoulDate = (value: Date): string => SEOUL_DATE.format(value);

/** 서울 날짜 YYYY-MM-DD 의 자정(KST) 시각. */
const seoulMidnight = (date: string): Date => new Date(`${date}T00:00:00+09:00`);

const shiftDays = (date: string, days: number): string =>
  seoulDate(new Date(seoulMidnight(date).getTime() + days * DAY_MS));

/** 프리셋의 기간 — 오늘은 하루, 7일·30일은 오늘을 포함한 N일. 기간 지정은 최근 7일을 출발점으로 둔다. */
export const presetSelection = (
  preset: DashboardPeriodPreset,
  now: Date,
): DashboardPeriodSelection => {
  const today = seoulDate(now);
  switch (preset) {
    case 'today':
      return { preset, from: today, to: today };
    case '30d':
      return { preset, from: shiftDays(today, -29), to: today };
    default:
      return { preset, from: shiftDays(today, -6), to: today };
  }
};

/** 날짜를 직접 고친 선택 — 거꾸로 넣으면 둘을 바꿔 항상 `from <= to` 가 되게 한다. */
export const customSelection = (from: string, to: string): DashboardPeriodSelection =>
  from <= to ? { preset: 'custom', from, to } : { preset: 'custom', from: to, to: from };

/** 서버 입력 — 반열림 `[from 자정, to 다음 날 자정)` 를 ISO(UTC)로. */
export const toSummaryInput = (selection: DashboardPeriodSelection): DashboardSummaryInput => ({
  period: {
    from: seoulMidnight(selection.from).toISOString(),
    to: seoulMidnight(shiftDays(selection.to, 1)).toISOString(),
  },
});

/** 화면에 보이는 범위 — 하루면 날짜 하나, 아니면 "시작 – 끝". */
export const periodLabel = (selection: DashboardPeriodSelection): string =>
  selection.from === selection.to ? selection.from : `${selection.from} – ${selection.to}`;
